import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import {
    Title,
    Button,
    ModalPanel,
    ExclusiveChoiceDropdown,
    Loader,
    Text
} from "dash-styled"
import {
    AIModel,
    AITrainingTemplate
} from "../../../../../../../../Models/API/AIModel"
import { useAppTemplate } from "../../../../../../../../Hooks/useAppTemplate"
import { Result } from "../../../../../../../../Models/Utility/Result"
import { hasValue } from "../../../../../../../../Models/Client/FetchState"
import { JSONEditor } from "../../../../../../../Common/Lazy/JSONEditor"
import { useBoolean } from "../../../../../../../../Hooks/useBoolean"

interface AddAITrainingInstanceModalProps {
    appID: string
    selectedTrainingTemplate: AITrainingTemplate
    modelChoices?: AIModel[]
    isProcessing: boolean
    onClose: () => void
    onSubmit?: (modelId: string, properties: unknown, taskName: string) => void
}

export const AddAITrainingInstanceModal: FC<AddAITrainingInstanceModalProps> =
    ({
        appID,
        selectedTrainingTemplate,
        modelChoices,
        isProcessing,
        onClose,
        onSubmit
    }) => {
        const [selectedModelIndex, setSelectedModelIndex] = useState<number>(-1)
        const [selectedTaskName, setSelectedTaskName] = useState<string>()
        const [selectedProperties, setSelectedProperties] = useState<unknown>()
        const [isPropertiesValid, setPropertiesValid, setPropertiesInValid] =
            useBoolean(true)
        const templateFetch = useAppTemplate(appID)
        const choices = useMemo(
            () => modelChoices?.map(model => `version ${model.version}`),
            [modelChoices]
        )

        const handlePropertiesChange = useCallback(
            (value: unknown) => {
                setSelectedProperties(value)
                setPropertiesValid()
            },
            [setPropertiesValid]
        )

        const taskNamesFetch = useMemo(
            () =>
                !hasValue(templateFetch)
                    ? templateFetch
                    : Result.ok(
                          Object.keys(
                              templateFetch.value.defaultConstraints
                                  .taskConstraints ?? {}
                          )
                      ),
            [templateFetch]
        )

        const selectedModel = modelChoices?.[selectedModelIndex]
        const handleSubmit = useMemo(() => {
            if (
                selectedModel === undefined ||
                selectedTaskName === undefined ||
                !isPropertiesValid ||
                onSubmit === undefined
            ) {
                return undefined
            }

            return () => {
                onSubmit(selectedModel.id, selectedProperties, selectedTaskName)
            }
        }, [
            onSubmit,
            selectedTaskName,
            selectedModel,
            selectedProperties,
            isPropertiesValid
        ])

        useEffect(() => {
            if (
                selectedModel !== undefined &&
                selectedModel.default_training_parameters !== null
            ) {
                setSelectedProperties(
                    selectedModel.default_training_parameters as string
                )
            }
        }, [selectedModel])

        const renderContent = () => {
            if (!hasValue(taskNamesFetch)) {
                return <Loader />
            }

            return (
                <>
                    {choices !== undefined ? (
                        <ExclusiveChoiceDropdown
                            label="Version"
                            placeholder="Choose a version"
                            choices={choices}
                            selectedIndex={selectedModelIndex}
                            onClick={setSelectedModelIndex}
                        />
                    ) : (
                        <Loader size="xxxl" className="mt-8" />
                    )}
                    <ExclusiveChoiceDropdown
                        className="mt-2"
                        label="Task"
                        placeholder="Choose a task for the model"
                        choices={taskNamesFetch.value}
                        value={selectedTaskName}
                        onClick={setSelectedTaskName}
                    />

                    {selectedModel !== undefined &&
                        selectedProperties !== undefined && (
                            <JSONEditor
                                key={selectedModel.id}
                                className="mt-2"
                                initialValue={selectedProperties}
                                heightPx={200}
                                onChange={handlePropertiesChange}
                                fallback={<Loader size="xxxl" />}
                                onError={setPropertiesInValid}
                            />
                        )}
                </>
            )
        }

        return (
            <ModalPanel onClose={onClose} className="max-w-2xl w-full">
                <Title variant="heading2" weight="semibold">
                    Select version to train
                </Title>
                <Text weight="semibold">
                    for: {selectedTrainingTemplate.name}
                </Text>
                {renderContent()}

                <div className="flex justify-center pt-4">
                    <Button
                        className="w-auto"
                        disabled={isProcessing || handleSubmit === undefined}
                        onClick={handleSubmit}
                    >
                        {isProcessing ? "Starting..." : "Start training"}
                    </Button>
                </div>
            </ModalPanel>
        )
    }
